import * as React from 'react';
import {view} from '@sail/ui';
import type {View} from '@sail/engine';
import {useIntl} from '@sail/intl';
import {customerLogoTitle} from 'mkt-ssr/components/sail/CustomerLogo/CustomerLogo.messages';

type Alignment = 'top' | 'bottom' | 'center';
type Justification = 'left' | 'right' | 'center';

interface LogoViewboxAlignments {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

const getPreserveAspectRatio = (
  align: Alignment,
  justify: Justification,
): string => {
  let xAlign = 'xMid';
  let yAlign = 'YMid';

  if (justify === 'left') xAlign = 'xMin';
  else if (justify === 'right') xAlign = 'xMax';

  if (align === 'top') yAlign = 'YMin';
  else if (align === 'bottom') yAlign = 'YMax';

  return `${xAlign}${yAlign} meet`;
};

const getViewBox = (
  justify: Justification,
  align: Alignment,
  width: number | string,
  height: number | string,
  alignments: LogoViewboxAlignments,
): string => {
  let x = 0;
  let y = 0;

  if (justify === 'left') x = alignments.left;
  else if (justify === 'right') x = alignments.right;
  else if (justify === 'center') x = 0;

  if (align === 'top') y = alignments.top;
  else if (align === 'bottom') y = alignments.bottom;
  else if (align === 'center') y = 0;

  return `${x} ${y} ${width} ${height}`;
};

export type CustomerLogoWrapperProps = View.IntrinsicElement<
  'svg',
  {
    align?: Alignment;
    justify?: Justification;
    logoViewboxAlignments?: LogoViewboxAlignments;
    title?: string;
  }
>;

const CustomerLogoWrapper: React.FC<
  React.PropsWithChildren<CustomerLogoWrapperProps>
> = ({
  children,
  align = 'center',
  justify = 'center',
  width = 200,
  height = 40,
  logoViewboxAlignments = {
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
  },
  title = '',
  ...props
}) => {
  const preserveAspectRatio = getPreserveAspectRatio(align, justify);
  const intl = useIntl();
  const viewBox = getViewBox(
    justify,
    align,
    width,
    height,
    logoViewboxAlignments,
  );

  return (
    <view.svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      preserveAspectRatio={preserveAspectRatio}
      viewBox={viewBox}
      {...props}
    >
      {children}
      <title>
        {intl.formatMessage(customerLogoTitle, {companyName: title})}
      </title>
    </view.svg>
  );
};

export default CustomerLogoWrapper;
